<mat-accordion [multi]="false" class="custom-accordion">
  <mat-expansion-panel
    *ngFor="let item of accordionItems; trackBy: trackByFn"
    class="custom-panel"
    [ngClass]="{
      'selected-panel': item.id === selectedId,
      'panel-reorder-mode': isReorderMode(item.id!),
    }"
    [expanded]="item.expanded"
    [disabled]="item.disabled"
    (opened)="onPanelOpened(item)"
    (closed)="onPanelClosed(item)"
  >
    <mat-expansion-panel-header class="panel-header">
      <div class="header-content">
        <span class="panel-title">{{ item.title }}</span>
      </div>

      <div class="header-actions">
        <!-- Re-Order button — only visible when accordion has ≥2 files/links.
             Uses mat-stroked-button with a text label to match the Banners /
             NoticeBoard pattern and give users a clear affordance. -->
        <button
          *ngIf="item.links && item.links.length > 1"
          mat-stroked-button
          class="reorder-toggle-btn me-1"
          [class.reorder-toggle-btn--active]="isReorderMode(item.id!)"
          [disabled]="isLoadingReorder(item.id!)"
          (click)="toggleLinkReorderMode(item, $event)"
        >
          <!-- [hidden] keeps both nodes in the DOM to avoid brief duplication
               during Angular's create/destroy cycle. -->
          <mat-spinner
            [hidden]="!isLoadingReorder(item.id!)"
            diameter="14"
          ></mat-spinner>
          <i
            [hidden]="isLoadingReorder(item.id!)"
            class="fa-solid me-1"
            [class.fa-arrows-up-down]="!isReorderMode(item.id!)"
            [class.fa-check]="isReorderMode(item.id!)"
          ></i>
          <span [hidden]="isLoadingReorder(item.id!)">
            {{ isReorderMode(item.id!) ? "Save Order" : "Re-Order" }}
          </span>
        </button>

        <button
          mat-icon-button
          class="action-btn"
          [matMenuTriggerFor]="actionMenu"
          [matTooltip]="'More Actions'"
          (click)="$event.stopPropagation()"
        >
          <i
            [class]="'action-icon ' + (item.actionIcon || 'fas fa-ellipsis-v')"
          ></i>
        </button>

        <mat-menu #actionMenu="matMenu" class="action-menu">
          <button
            mat-menu-item
            (click)="onMenuAction('addAccordion', item, $event)"
          >
            <i class="fas fa-plus menu-icon"></i> <span>Add Accordion</span>
          </button>
          <button
            mat-menu-item
            (click)="onMenuAction('uploadFile', item, $event)"
          >
            <i class="fas fa-upload menu-icon"></i> <span>Upload File</span>
          </button>
          <button mat-menu-item (click)="onMenuAction('link', item, $event)">
            <i class="fas fa-link menu-icon"></i> <span>Add Link</span>
          </button>
          <button mat-menu-item (click)="onMenuAction('edit', item, $event)">
            <i class="fas fa-edit menu-icon"></i> <span>Edit</span>
          </button>
          <button mat-menu-item (click)="onMenuAction('delete', item, $event)">
            <i class="fas fa-trash menu-icon"></i> <span>Delete</span>
          </button>
          <ng-container *ngIf="item.menuActions">
            <mat-divider></mat-divider>
            <button
              *ngFor="let menuAction of item.menuActions"
              mat-menu-item
              (click)="onMenuAction(menuAction.action, item, $event)"
              [disabled]="menuAction.disabled"
            >
              <i [class]="'menu-icon ' + menuAction.icon"></i>
              <span>{{ menuAction.label }}</span>
            </button>
          </ng-container>
        </mat-menu>
      </div>
    </mat-expansion-panel-header>

    <!-- Files / Links list -->
    <ng-container *ngIf="item.links && item.links.length > 0">
      <div
        class="links-section"
        cdkDropList
        [cdkDropListDisabled]="!isReorderMode(item.id!)"
        (cdkDropListDropped)="dropLink($event, item)"
      >
        <div
          *ngFor="let link of item.links; trackBy: trackLinkByFn"
          class="link-item px-2 d-flex align-items-center"
          [class.reorder-active]="isReorderMode(item.id!)"
          cdkDrag
          [cdkDragDisabled]="!isReorderMode(item.id!)"
        >
          <!-- Drag handle: [hidden] avoids *ngIf create/destroy cycle -->
          <mat-icon
            class="drag-handle me-2"
            [hidden]="!isReorderMode(item.id!)"
            cdkDragHandle
            >drag_indicator</mat-icon
          >

          <!-- File / Link row -->
          <a
            [href]="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-grow-1 d-flex align-items-center text-decoration-none link-row"
            (click)="onLinkClick($event, link)"
          >
            <i *ngIf="link.icon" [class]="'link-icon me-2 ' + link.icon"></i>
            <!-- Show the human-readable label; fall back to URL only if no label -->
            <span
              class="link-label"
              [matTooltip]="link.url"
              matTooltipPosition="above"
              >{{ link.label || link.url }}</span
            >
          </a>

          <!-- ⋮ actions menu — hidden while reordering -->
          <button
            *ngIf="!isReorderMode(item.id!)"
            mat-icon-button
            class="link-action-btn"
            [matMenuTriggerFor]="linkMenu"
            (click)="$event.stopPropagation()"
          >
            <i class="fas fa-ellipsis-v"></i>
          </button>

          <mat-menu #linkMenu="matMenu">
            <button mat-menu-item (click)="onLinkMenuAction('view', link)">
              <i class="fas fa-eye me-2"></i><span>View</span>
            </button>
            <button mat-menu-item (click)="onLinkMenuAction('delete', link)">
              <i class="fas fa-trash me-2"></i><span>Delete</span>
            </button>
          </mat-menu>
        </div>
      </div>
    </ng-container>

    <!-- Nested accordion panels -->
    <ng-container *ngIf="item.children && item.children.length > 0">
      <app-expansion-panel
        [items]="item.children"
        [nestedLevel]="nestedLevel + 1"
        [selectedId]="selectedId"
        (itemExpanded)="itemExpanded.emit($event)"
        (itemCollapsed)="itemCollapsed.emit($event)"
        (linkClicked)="linkClicked.emit($event)"
        (actionClicked)="actionClicked.emit($event)"
        (filesReordered)="filesReordered.emit($event)"
      ></app-expansion-panel>
    </ng-container>
  </mat-expansion-panel>
</mat-accordion>
